import { useState } from 'react';
import { Form, Input, Modal, notification } from 'antd';
import CustomButton from 'components/custom-button';
import type { StudioCandidate, StudioMembersChange } from '../contract';
import { addStudioMembers } from '../api';
import { mapStudioError } from '../studio-error';
import { REASON_MAX } from '../labels';
import MemberPicker, { candidatesToPayload } from './member-picker';

export interface AddMembersModalProps {
  open: boolean;
  studioId: number;
  studioTitle?: string | null;
  onClose: () => void;
  onDone?: (result: StudioMembersChange) => void;
}

interface FormValues {
  candidates?: StudioCandidate[];
  reason?: string;
}

/**
 * Admin thêm thành viên vào Studio (S11, mục 8.7.5): chọn nhiều bằng ô chọn S26, cả tài khoản lẫn Creator
 * chỉ có UID (tối đa 100 mỗi loại). Người đang ở Studio khác được chuyển sang Studio này (máy chủ ghi
 * STUDIO_MEMBER_MOVE). Không dùng AddMembersForm cũ (chỉ chọn được id tài khoản, nhãn có email).
 */
const AddMembersModal = ({ open, studioId, studioTitle, onClose, onDone }: AddMembersModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const picked = Form.useWatch('candidates', form) || [];
  const counts = candidatesToPayload(picked);

  const handleClose = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: FormValues) => {
    const { userIds, uids } = candidatesToPayload(values.candidates || []);
    if (!userIds.length && !uids.length) {
      notification.error({ message: 'Chọn ít nhất một người.' });
      return;
    }
    setSaving(true);
    try {
      const result = await addStudioMembers(studioId, {
        userIds: userIds.length ? userIds : undefined,
        uids: uids.length ? uids : undefined,
        reason: values.reason?.trim() || undefined,
      });
      const moved = result?.moved ? `, chuyển ${result.moved} người từ Studio khác` : '';
      notification.success({ message: `Đã thêm ${result?.added ?? 0} người${moved}.` });
      form.resetFields();
      onDone?.(result);
      onClose();
    } catch (err) {
      notification.error({ message: mapStudioError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={studioTitle ? `Thêm thành viên · ${studioTitle}` : 'Thêm thành viên'}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={640}
    >
      <Form form={form} layout="vertical" className="custom-form" onFinish={onFinish}>
        <Form.Item
          label="Thành viên"
          name="candidates"
          extra={`Tìm theo tên, TikTok ID hoặc UID (ít nhất 2 ký tự). Đã chọn ${counts.userIds.length} tài khoản, ${counts.uids.length} Creator chưa có tài khoản.`}
          rules={[{ required: true, type: 'array', min: 1, message: 'Chọn ít nhất một người.' }]}
        >
          <MemberPicker studioId={studioId} multiple />
        </Form.Item>
        <Form.Item label="Lý do (không bắt buộc)" name="reason">
          <Input.TextArea rows={2} maxLength={REASON_MAX} showCount placeholder="Nhập lý do" />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text="Thêm vào Studio"
            bgColor="secondary-green"
            htmlType="submit"
            loading={saving}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default AddMembersModal;
